import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../config/api";

const AuthContext = createContext(null);

// View modes for God Mode
export const VIEW_MODES = {
  GOD: "god",           // See everything
  STAFF: "staff",       // Europacific staff view
  CUSTOMER: "customer", // Customer view
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(localStorage.getItem("viewMode") || VIEW_MODES.GOD);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem("token");
      if (storedToken) {
        try {
          const response = await axios.get(`${API_URL}/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` },
          });
          setUser(response.data);
          setToken(storedToken);
        } catch (error) {
          localStorage.removeItem("token");
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  // Persist view mode
  useEffect(() => {
    localStorage.setItem("viewMode", viewMode);
  }, [viewMode]);

  // Set auth data directly (used after 2FA verification)
  const setAuthData = (accessToken, userData) => {
    localStorage.setItem("token", accessToken);
    setToken(accessToken);
    setUser(userData);
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, {
      email,
      password,
    });
    
    // Check if 2FA is required
    if (response.data.requires_2fa) {
      return { requires_2fa: true, email: response.data.email };
    }
    
    // Direct login (if 2FA is disabled for some reason)
    const { access_token, user: userData } = response.data;
    setAuthData(access_token, userData);
    return userData;
  };

  const register = async (data) => {
    const response = await axios.post(`${API_URL}/auth/register`, data);
    
    // New registration flow: returns pending status with PUK
    if (response.data.status === "pending_approval") {
      return {
        pending: true,
        puk: response.data.puk,
        message: response.data.message,
        puk_message: response.data.puk_message
      };
    }
    
    // Legacy flow (if any)
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${token}`,
  });

  const isEuropacificUser = () => {
    return user?.user_type === "europacific" || user?.user_type === "europacific_staff" || user?.email?.endsWith("@europacific.com");
  };

  // God Mode: Check if user can access God Mode (must be admin)
  const canUseGodMode = user?.is_admin === true;

  // Get effective view based on viewMode and actual permissions
  const getEffectiveView = () => {
    if (!canUseGodMode) {
      // Non-admin users always see their actual view
      return isEuropacificUser() ? VIEW_MODES.STAFF : VIEW_MODES.CUSTOMER;
    }
    return viewMode;
  };

  // Check if should show staff features (in god mode or staff view)
  const showStaffFeatures = () => {
    const effective = getEffectiveView();
    return effective === VIEW_MODES.GOD || effective === VIEW_MODES.STAFF;
  };

  // Check if should show customer features (in god mode or customer view)
  const showCustomerFeatures = () => {
    const effective = getEffectiveView();
    return effective === VIEW_MODES.GOD || effective === VIEW_MODES.CUSTOMER;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        login,
        register,
        logout,
        setAuthData,
        getAuthHeaders,
        isAuthenticated: !!token,
        isAdmin: user?.is_admin || false,
        isEuropacificUser: isEuropacificUser(),
        userType: user?.user_type || "customer",
        // God Mode features
        viewMode,
        setViewMode,
        canUseGodMode,
        getEffectiveView,
        showStaffFeatures,
        showCustomerFeatures,
        VIEW_MODES,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
